import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PoliticalMentionsChart from 'components/ContentRelated/PoliticalMentionsWidget/PoliticalMentionsChart';
import getAppSite from 'utils/getAppSite';

const MentionsGraphHoverLink = (props) => {
  const {
    isOpen,
    closeFunction,
    keyword,
    hoverMentionsGraphBox,
    containerRef,
    data,
    coordinates,
    onHoverEnter,
    onHoverLeave,
    isAuthenticated,
  } = props;
  const chartRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  // Function to trigger animation
  const triggerAnimation = () => {
    if (chartRef.current) {
      const chart = chartRef.current;

      // Get the original data
      const originalData = chart.data.datasets[0].data;

      // Set initial data to start from bottom (0 values)
      const initialData = originalData.map(() => 0);
      chart.data.datasets[0].data = initialData;
      chart.update('none'); // Update without animation first

      // Animate to original values with rising effect
      setTimeout(() => {
        chart.data.datasets[0].data = originalData;
        chart.update('active');
      }, 10);
    }
  };

  // Trigger animation once per open
  useEffect(() => {
    if (isOpen && chartRef.current && !hasAnimatedRef.current) {
      const timer = setTimeout(() => {
        triggerAnimation();
        hasAnimatedRef.current = true;
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset the one-time flag when closed
  useEffect(() => {
    if (!isOpen) {
      hasAnimatedRef.current = false;
    }
  }, [isOpen]);

  const content = (
    <div
      className='position-fixed hover-mentions-graph-message-container z-extra-max'
      ref={hoverMentionsGraphBox}
      style={{
        visibility: isOpen ? 'visible' : 'hidden',
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? 'translateY(0)' : 'translateY(10px)',
        ...coordinates,
      }}
      onMouseEnter={() => {
        if (onHoverEnter) onHoverEnter();
      }}
      onMouseLeave={(e) => {
        if (onHoverLeave) onHoverLeave();
        const related = e && 'relatedTarget' in e ? e.relatedTarget : null;
        const containerEl = containerRef && containerRef.current ? containerRef.current : null;
        if (!containerEl) {
          closeFunction();
          return;
        }
        if (!(related instanceof Node)) {
          closeFunction();
          return;
        }
        if (!containerEl.contains(related)) {
          closeFunction();
        }
      }}
    >
      <div className='bg-white box-rounded hover-mentions-graph-message'>
        <div
          className={`py-3 pl-3 pr-3 mb-3 box-rounded bg-white box-political-mentions-widget z-10 position-relative`}
        >
          <h3 className={`font-weight-normal mb-0`}>
            <div className={`flex-centered mb-0 justify-content-between summary-header-container gap-10`}>
              <div
                className={`flex-grow-1 summary-content-dropdown-component-container agency-user-summary flex-centered`}
              >
                <span className='font-weight-bold' style={{ lineHeight: '33.6px' }}>
                  {getAppSite() === 'usa' ? 'Mentions in Congress' : 'Political interest'}:
                </span>
                <div className={`tailor-summaries-dropdown-component specific-left-padding-4 hidden-lines`}>
                  {keyword}
                </div>
              </div>
              <span
                className='icon-info d-inline-block-centered ml-1'
                style={{ marginTop: '-1px' }}
                data-tooltip-content={`Displaying the total number of mentions by policymakers in ${
                  getAppSite() === 'usa' ? 'Congress' : 'Parliament'
                } in the last six months. Click the graph for full analytics`}
                data-tooltip-id='general-tooltip'
                data-tooltip-class-name={'general-tooltip pricing-feature-tooltip report-popup-tooltip'}
              />
            </div>
          </h3>
          <PoliticalMentionsChart
            data={data}
            enableAnalytics={true}
            keyword={keyword}
            graphOptions={{
              // Disable default animations to avoid re-triggers on prop updates
              animation: { duration: 0 },
              // Define a custom transition used only by our manual update('active')
              transitions: {
                active: {
                  animation: { duration: 600, easing: 'easeInOutExpo', loop: false, delay: 600 },
                  animations: { y: { from: 4 } },
                },
              },
            }}
            chartRef={chartRef}
            isAuthenticated={isAuthenticated}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default MentionsGraphHoverLink;
